import { portalApi } from "@services/api/portalApi";
import { apiC } from "@services/api/apiC";
import type { MessageFormat } from "./messageDocument";
import {
	type ConversationCourse,
	type Conversation,
	type CreateConversationForStudentRequestFlat,
	type CreateMessageForConversationRequestFlat,
	type GetConversationsForStudentRequestFlat,
	type GetConversationTypesForStudentRequestFlat,
	type GetMessagesForConversationRequestFlat,
	type GetMessagesForConversationSuccess,
	type ConversationType,
	type GetStudentInterventionSummaryRequestFlat,
	type StudentInterventionSummary,
	type UpdateStudentInterventionSummaryRequestFlat,
	type UpdateStudentInterventionSummarySuccess,
	ConversationMessage,
} from "@godalming/api-types";

export type ConversationSummary = Conversation & { unreadCount?: number };

export async function fetchStudentConversations(
	params: GetConversationsForStudentRequestFlat,
	usernameOverride: string | null,
): Promise<ConversationSummary[]> {
	try {
		const response = await portalApi.get<ConversationSummary[]>(
			`/conversations/students/${params.studentNo}`,
			{
				params: {
					...params,
					usernameOverride: usernameOverride ?? "",
				},
			},
		);
		return response.data;
	} catch (error) {
		console.error("Error fetching student conversations:", error);
		throw error;
	}
}

export async function fetchConversationMessages(
	params: GetMessagesForConversationRequestFlat,
): Promise<GetMessagesForConversationSuccess> {
	try {
		const response = await portalApi.get<ConversationMessage[]>(
			`/conversations/${params.conversationId}/messages`,
		);
		return response.data;
	} catch (error) {
		console.error("Error fetching conversation messages:", error);
		throw error;
	}
}

export async function fetchStudentConversationCreateTypeList(
	params: GetConversationTypesForStudentRequestFlat,
): Promise<ConversationType[]> {
	try {
		const response = await apiC.get(`/StudentConversationCreateTypeList`, {
			params: {
				...params,
			},
		});
		return response.data.studentConversationCreateList as ConversationType[];
	} catch (error) {
		console.error(
			"Error fetching student conversation create type list:",
			error,
		);
		throw error;
	}
}

export async function fetchStudentConversationCourseList(
	params: GetConversationTypesForStudentRequestFlat,
): Promise<ConversationCourse[]> {
	try {
		const response = await apiC.get(`/StudentConversationCreateTypeList`, {
			params: {
				...params,
			},
		});
		return response.data.studentConversationCourseList as ConversationCourse[];
	} catch (error) {
		console.error("Error fetching student conversation course list:", error);
		throw error;
	}
}

export async function postConversationMessage(
	params: CreateMessageForConversationRequestFlat & {
		messageFormat?: MessageFormat;
	},
) {
	try {
		const response = await portalApi.post(
			`/conversations/${params.conversationId}/messages`,
			{ message: params.message, messageFormat: params.messageFormat },
		);
		return response.data;
	} catch (error) {
		console.error("Error posting conversation message:", error);
		throw error;
	}
}

export async function postConversation(
	params: CreateConversationForStudentRequestFlat & {
		messageFormat?: MessageFormat;
	},
) {
	try {
		const response = await portalApi.post<number>(
			`/conversations/students/${params.studentNo}`,
			{
				typeCfCId: params.typeCfCId,
				studentClassId: params.studentClassId ?? null,
				title: params.title,
				message: params.message,
				messageFormat: params.messageFormat,
			},
		);
		return String(response.data);
	} catch (error) {
		console.error("Error posting conversation:", error);
		throw error;
	}
}

export async function fetchStudentInterventionSummary(
	params: GetStudentInterventionSummaryRequestFlat,
): Promise<StudentInterventionSummary> {
	try {
		const response = await apiC.get(`/StudentSummaryNeedIntervention`, {
			params: {
				...params,
			},
		});
		return response.data as StudentInterventionSummary;
	} catch (error) {
		console.error("Error fetching student intervention summary:", error);
		throw error;
	}
}

export async function updateStudentInterventionSummary(
	params: UpdateStudentInterventionSummaryRequestFlat,
): Promise<UpdateStudentInterventionSummarySuccess> {
	try {
		const response = await apiC.post(`/StudentSummaryNeedInterventionUpdate`, {
			...params,
		});
		return response.data as UpdateStudentInterventionSummarySuccess;
	} catch (error) {
		console.error("Error updating student intervention summary:", error);
		throw error;
	}
}
